import { IPatientWrite, IPatientWriteBody, PatientKind } from "common/types";
import { FetchError } from "common/fetch/types";
import { Optional } from "common/utils/types";

import { IClient, PasswordCondition } from "../types";
import cat from "../images/species/cat.svg";
import catBlue from "../images/species/cat-blue.svg";
import dog from "../images/species/dog.svg";
import dogBlue from "../images/species/dog-blue.svg";
import paw from "../images/species/paw.svg";
import pawBlue from "../images/species/paw-blue.svg";
import horse from "../images/species/horse.svg";
import horseBlue from "../images/species/horse-blue.svg";

export const passwordConditions: PasswordCondition[] = [
  { label: "Password must be 8 characters long", check: (p) => p.length >= 8 },
];

export const usernameLockTooltip =
  "This field matches the username on file with your clinic and cannot be edited.";

type SignupChannel = "phone" | "email";

/**
 * Per-form lock state for a signup form, given the session's client (if any) and the
 * channel the form uses. Keyed off the on-file contact, not the auth source: the backend
 * enforces `client_owns_contact` for any attached client (not just magic links), and the
 * client query only returns contacts for an authenticated, not-yet-signed-up visitor.
 *
 *  - `redirectToOtherForm`: this channel isn't on file but the other is — send them there.
 *  - `lockUsername`: this channel is on file — lock its field.
 *  - `canSwitchChannel`: false only when pinned to a single on-file channel.
 */
export const getSignupChannelState = (
  client: IClient | undefined,
  formChannel: SignupChannel,
): { redirectToOtherForm: boolean; lockUsername: boolean; canSwitchChannel: boolean } => {
  const hasPhoneOnFile = !!client?.primary_contact_number?.number;
  const hasEmailOnFile = !!client?.primary_contact_email?.email;
  const thisChannelOnFile = formChannel === "phone" ? hasPhoneOnFile : hasEmailOnFile;
  const otherChannelOnFile = formChannel === "phone" ? hasEmailOnFile : hasPhoneOnFile;

  return {
    redirectToOtherForm: !thisChannelOnFile && otherChannelOnFile,
    lockUsername: thisChannelOnFile,
    canSwitchChannel: hasPhoneOnFile === hasEmailOnFile,
  };
};

export const patientWriteToPatientUpdateBody = (
  values: Partial<IPatientWrite>,
): Partial<IPatientWriteBody> => {
  const { primary_breed: primaryBreed, secondary_breeds: secondaryBreeds, color, ...rest } = values;

  return {
    ...rest,
    ...(primaryBreed && {
      primary_breed: primaryBreed.value,
    }),
    ...(secondaryBreeds && {
      secondary_breeds: secondaryBreeds.map((b) => b.value),
    }),
    ...(color && {
      color: color.value,
    }),
  };
};

/**
 * The signup failure message shown to the user: the first non-field error if
 * present, otherwise the top-level detail. Shared between the signup modals and
 * the analytics `error_message` so both report the same string.
 */
export const getSignupErrorDisplay = (error: Optional<FetchError>): string | null =>
  error?.errors?.non_field_errors?.[0] ?? error?.detail ?? null;

export const imageForSpecies = (species: PatientKind, isActive: boolean) => {
  switch (species) {
    case PatientKind.Feline:
      return isActive ? catBlue : cat;
    case PatientKind.Canine:
      return isActive ? dogBlue : dog;
    case PatientKind.Equine:
      return isActive ? horseBlue : horse;
    default:
      return isActive ? pawBlue : paw;
  }
};
